import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import { useCart } from '../../context/CartContext';
import { Helmet } from 'react-helmet';
import BackgroundImage from '../../images/yale_golf_course.jpg';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || (
  typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? `${window.location.origin}/api`
    : 'http://localhost:4000'
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const page = {
  backgroundImage: `linear-gradient(rgba(13,24,16,0.55), rgba(13,24,16,0.85)), url(${BackgroundImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  minHeight: '100vh',
};

const wrap = {
  paddingTop: '125px',
  paddingBottom: '80px',
  display: 'flex',
  justifyContent: 'center',
};

const inner = { width: '90%', maxWidth: '860px' };

const breadcrumb = {
  color: '#C4A875',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 500,
  fontSize: '13px',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  marginBottom: '32px',
};

const breadcrumbLink = { color: '#C4A875', textDecoration: 'none' };
const breadcrumbSep  = { color: '#B8892A', margin: '0 6px' };
const breadcrumbActive = { color: '#F5EFE3' };

const pageTitle = {
  fontFamily: 'Playfair Display, Georgia, serif',
  fontSize: '38px',
  fontWeight: 700,
  color: '#F5EFE3',
  marginBottom: '32px',
};

const divider = { border: 'none', borderTop: '1px solid rgba(184,137,42,0.25)', margin: '0 0 24px' };

const emptyWrap = { display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '60px', gap: '24px' };
const emptyText  = { color: '#C4A875', fontFamily: 'Raleway, sans-serif', fontSize: '18px', textAlign: 'center' };

const row = {
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  padding: '20px 0',
  borderBottom: '1px solid rgba(184,137,42,0.15)',
};

const itemImg = { width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px', boxShadow: '0 2px 10px rgba(0,0,0,0.4)', flexShrink: 0 };
const itemInfo = { flex: 1 };
const itemName = { fontFamily: 'Playfair Display, Georgia, serif', fontSize: '18px', fontWeight: 600, color: '#F5EFE3', marginBottom: '4px' };
const itemUnitPrice = { fontFamily: 'Raleway, sans-serif', fontSize: '14px', color: '#B8892A', marginBottom: '10px' };

const qtyRow = { display: 'flex', alignItems: 'center', gap: '10px' };
const qtyBtn = {
  width: '30px', height: '30px', background: 'transparent',
  border: '1px solid rgba(184,137,42,0.5)', borderRadius: '4px',
  color: '#B8892A', fontSize: '18px', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
};
const qtyInput = {
  width: '44px', height: '30px', background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(184,137,42,0.4)', borderRadius: '4px',
  color: '#F5EFE3', fontFamily: 'Raleway, sans-serif', fontSize: '14px', textAlign: 'center', outline: 'none',
};

const itemLineTotal = { fontFamily: 'Raleway, sans-serif', fontWeight: 600, fontSize: '18px', color: '#B8892A', minWidth: '80px', textAlign: 'right' };
const removeBtn = { background: 'transparent', border: 'none', color: '#8A8A82', fontSize: '22px', cursor: 'pointer', padding: '4px', lineHeight: 1 };

const summaryWrap = { marginTop: '32px', display: 'flex', justifyContent: 'flex-end' };
const summaryBox = { background: 'rgba(13,24,16,0.6)', border: '1px solid rgba(184,137,42,0.25)', borderRadius: '6px', padding: '28px 32px', minWidth: '280px' };

const summaryRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', fontFamily: 'Raleway, sans-serif', color: '#C4A875', fontSize: '14px' };
const summaryTotal = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '16px', borderTop: '1px solid rgba(184,137,42,0.25)', fontFamily: 'Raleway, sans-serif', fontWeight: 700, fontSize: '22px', color: '#F5EFE3' };

const checkoutBtnBase = { marginTop: '20px', width: '100%', padding: '14px', background: '#B8892A', color: '#F5EFE3', border: '2px solid #B8892A', borderRadius: '4px', fontFamily: 'Raleway, sans-serif', fontWeight: 600, fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' };
const clearBtnBase   = { marginTop: '12px', width: '100%', padding: '10px', background: 'transparent', color: '#8A8A82', border: '1px solid rgba(184,137,42,0.3)', borderRadius: '4px', fontFamily: 'Raleway, sans-serif', fontWeight: 600, fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' };
const shopBtnBase    = { display: 'inline-block', padding: '12px 32px', background: 'transparent', color: '#B8892A', border: '2px solid #B8892A', borderRadius: '4px', fontFamily: 'Raleway, sans-serif', fontWeight: 600, fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'none' };
const errorText = { color: '#e57373', fontFamily: 'Raleway, sans-serif', fontSize: '14px', marginTop: '12px', textAlign: 'center' };

// ─── Component ───────────────────────────────────────────────────────────────

function CartPage() {
  const [isOpen, setIsOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  const { cart, cartTotal, removeFromCart, updateQuantity, clearCart, cartLoading, userId } = useCart();

  const toggle = () => setIsOpen((o) => !o);
  const fmt    = (cents) => '$' + (cents / 100).toFixed(2);

  const handleCheckout = async () => {
    setCheckoutError('');
    setCheckoutLoading(true);
    try {
      const res  = await fetch(`${SERVER_URL}/create-checkout-session`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          items: cart.map((i) => ({ itemId: i.itemId, quantity: i.quantity })),
          cartUserId: userId || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Checkout failed. Please try again.');
      window.location.href = data.url; // redirect to Stripe hosted checkout
    } catch (err) {
      setCheckoutError(err.message);
      setCheckoutLoading(false);
    }
  };

  const handleQtyChange = (itemId, raw) => {
    const n = parseInt(raw, 10);
    if (!isNaN(n) && n >= 1) updateQuantity(itemId, n);
  };

  return (
    <>
      <Helmet><title>Cart – Fairway Hunters</title></Helmet>
      <Sidebar isopen={isOpen} toggle={toggle} />
      <Navbar toggle={toggle} />

      <div style={page}>
        <div style={wrap}>
          <div style={inner}>

            {/* Breadcrumb */}
            <h3 style={breadcrumb}>
              <a href='/' style={breadcrumbLink}>Home</a>
              <span style={breadcrumbSep}>&#47;</span>
              <a href='/shop' style={breadcrumbLink}>Shop</a>
              <span style={breadcrumbSep}>&#47;</span>
              <span style={breadcrumbActive}>Cart</span>
            </h3>

            <h1 style={pageTitle}>Your Cart</h1>
            <hr style={divider} />

            {cartLoading ? (
              <p style={emptyText}>Loading…</p>
            ) : cart.length === 0 ? (
              <div style={emptyWrap}>
                <p style={emptyText}>Your cart is empty.</p>
                <a href='/shop' style={shopBtnBase}>Browse the Shop</a>
              </div>
            ) : (
              <>
                {cart.map((item) => (
                  <div key={item.itemId} style={row}>
                    {item.images?.[0] && (
                      <img src={`/${item.images[0]}`} alt={item.name} style={itemImg} />
                    )}
                    <div style={itemInfo}>
                      <p style={itemName}>{item.name}</p>
                      <p style={itemUnitPrice}>{fmt(item.price)} each</p>
                      <div style={qtyRow}>
                        <button style={qtyBtn} onClick={() => updateQuantity(item.itemId, item.quantity - 1)}>−</button>
                        <input type='text' value={item.quantity} onChange={(e) => handleQtyChange(item.itemId, e.target.value)} style={qtyInput} />
                        <button style={qtyBtn} onClick={() => updateQuantity(item.itemId, item.quantity + 1)}>+</button>
                      </div>
                    </div>
                    <p style={itemLineTotal}>{fmt(item.price * item.quantity)}</p>
                    <button
                      style={removeBtn}
                      title='Remove item'
                      onClick={() => removeFromCart(item.itemId)}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#e57373')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = '#8A8A82')}
                    >×</button>
                  </div>
                ))}

                {/* Summary */}
                <div style={summaryWrap}>
                  <div style={summaryBox}>
                    <div style={summaryRowStyle}>
                      <span>Subtotal</span><span>{fmt(cartTotal)}</span>
                    </div>
                    <div style={summaryRowStyle}>
                      <span>Shipping</span><span style={{ color: '#F5EFE3' }}>Calculated at checkout</span>
                    </div>
                    <div style={summaryTotal}>
                      <span>Total</span><span style={{ color: '#B8892A' }}>{fmt(cartTotal)}</span>
                    </div>

                    <button
                      style={{ ...checkoutBtnBase, opacity: checkoutLoading ? 0.6 : 1, cursor: checkoutLoading ? 'not-allowed' : 'pointer' }}
                      disabled={checkoutLoading}
                      onClick={handleCheckout}
                    >
                      {checkoutLoading ? 'Redirecting…' : 'Proceed to Checkout'}
                    </button>

                    {checkoutError && <p style={errorText}>{checkoutError}</p>}

                    <button
                      style={clearBtnBase}
                      onClick={() => { if (window.confirm('Clear all items from your cart?')) clearCart(); }}
                    >
                      Clear Cart
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default CartPage;